import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import {
  defineMessages,
  useIntl,
  type MessageTag,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { capitalizeFirstLetter, LoadingPlaceholder, theme, useResponsive } from '@sg/garnish';
import { CardExpirationState, FlexPaymentMethod } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';

import { useFlexCustomer } from '../../hooks';
import { SavedPaymentMethod } from '../SavedPaymentMethod';

export const AdditionalPaymentMethods = () => {
  const router = useRouter();

  const { isLoadingFlexCustomer, hasFlexPaymentMethod, flexPaymentMethod } = useFlexCustomer();

  const { track } = useTelemetry();
  const { match } = useResponsive();

  const { formatMessage } = useIntl();
  const unlinkedText = match([
    formatMessage(messages.flexUnlinkedXS, { br: renderBreak }),
    formatMessage(messages.flexUnlinkedXS, { br: renderBreak }),
    formatMessage(messages.flexUnlinkedLG),
  ]);

  const flexText = hasFlexPaymentMethod
    ? formatMessage(messages.flexLinked, { payment: formatFlexPaymentMethod(flexPaymentMethod) })
    : unlinkedText;

  const expirationState = getExpirationState(flexPaymentMethod);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handleLinkFlexAccount = useCallback(() => {
    track('payments.hsafsa.pressed');
    router.navigate('/hsa-fsa');
  }, [router, track]);

  if (isLoadingFlexCustomer) {
    return <LoadingPlaceholder rows={3} rowHeight={32} palette="cream" />;
  }

  return (
    <View style={styles.container}>
      <SavedPaymentMethod
        icon="IconFlex"
        description={flexText}
        a11yLabel={formatMessage(messages.flexAccessibilityLabel)}
        isSuccess={hasFlexPaymentMethod}
        isExpired={expirationState === CardExpirationState.Expired}
        isExpiring={expirationState === CardExpirationState.Nearing}
        onPress={handleLinkFlexAccount}
      />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly flexAccessibilityLabel: NoMessageValues;
  readonly flexUnlinkedXS: { readonly br: MessageTag };
  readonly flexUnlinkedLG: NoMessageValues;
  readonly flexLinked: { readonly payment: MessageValue };
}>({
  flexAccessibilityLabel: {
    defaultMessage: 'Link your Flex account',
    description: 'Additional Payment Methods | Flex Accessibility Label',
  },
  flexUnlinkedXS: {
    defaultMessage: 'Link your Flex account - <br></br>pay using your HSA / FSA',
    description: 'Additional Payment Methods | Flex Unlinked',
  },
  flexUnlinkedLG: {
    defaultMessage: 'Link your Flex account - pay using your HSA / FSA',
    description: 'Additional Payment Methods | Flex Unlinked',
  },
  flexLinked: {
    defaultMessage: 'HSA/FSA - {payment}',
    description: 'Additional Payment Methods | Flex Linked',
  },
});

function formatFlexPaymentMethod(paymentMethod?: FlexPaymentMethod | null) {
  if (!paymentMethod?.brand || !paymentMethod?.last4) return '';

  const brand = capitalizeFirstLetter(paymentMethod.brand.toLowerCase().replaceAll('_', ''));

  return `${brand} ${paymentMethod.last4}`;
}

function getExpirationState(paymentMethod?: FlexPaymentMethod | null) {
  if (!paymentMethod?.expYear || !paymentMethod?.expMonth) return CardExpirationState.Ok;

  const expirationYear = Number(paymentMethod.expYear);
  const expirationMonth = Number(paymentMethod.expMonth);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  if (currentYear > expirationYear) {
    return CardExpirationState.Expired;
  }

  if (currentYear === expirationYear) {
    if (currentMonth > expirationMonth) return CardExpirationState.Expired;
    return CardExpirationState.Nearing;
  }

  return CardExpirationState.Ok;
}

function renderBreak() {
  return '\n';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing['6'],
    backgroundColor: theme.colors.OPACITY.WHITE.DARKEST,
    borderRadius: theme.radius.large,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
  },
});
